import { useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import { PanelOption, Path } from './Dropdown';
import DropdownContext from './DropdownContext';
import { initialPanelState, panelReducer, PanelActionTypes } from './PanelContext';
import { formatOptions } from './utils/formatOptions';

const usePanelState = () => {
  const [state, dispatch] = useReducer(panelReducer, initialPanelState);
  const {
    dropdownActions: {
      applySelection,
      setFocusedIndex,
      setIsFilterFocused,
      setPressedKey,
      setSelection,
    },
    props: dropdownProps,
    state: dropdownState,
    uniqueId,
  } = useContext(DropdownContext);

  const endRef = useRef(null);
  const scrollRef = useRef(null);

  const {
    arrowDirection,
    filterHtmlValue,
    filterString,
    isActionsFocused,
    page,
    parentName,
    path,
  } = state;

  const {
    filterBy,
    filterFunction,
    matchBy,
    onCategorySelect,
    onNextPage,
    options,
    rowsPerPage,
    showGroups,
  } = dropdownProps;

  const { isFilterFocused } = dropdownState;

  const formattedOptions = formatOptions(options, parentName, showGroups);
  const filteredOptions = filterFunction(formattedOptions, filterString, filterBy, matchBy);

  const getLimit = useCallback(() => page * rowsPerPage + rowsPerPage, [page, rowsPerPage]);
  const paginated = filteredOptions.slice(0, getLimit());

  const handleScroll = useCallback(() => {
    const scrollPos =
      scrollRef.current.getBoundingClientRect().top +
      scrollRef.current.getBoundingClientRect().height;
    const endInView = endRef.current && scrollPos > endRef.current.getBoundingClientRect().top;
    if (endInView) {
      if (getLimit() <= filteredOptions.length) {
        dispatch({ type: PanelActionTypes.SET_PAGE, payload: page + 1 });
        onNextPage(filterHtmlValue);
      }
    }
  }, [filterHtmlValue, filteredOptions, getLimit, onNextPage, page]);

  const indexOfOptionThatStartsWith = useCallback(
    (key) => {
      const matches = paginated.reduce((acc, curr, idx) => {
        const currName = curr.name.toLowerCase();
        const substr = key.toLowerCase();
        if (currName.startsWith(substr)) acc.push(idx);
        return acc;
      }, []);

      if (matches.length) {
        return matches[0] + 1;
      }
      return 1;
    },
    [paginated]
  );

  if (dropdownState.pressedKey) {
    setFocusedIndex(indexOfOptionThatStartsWith(dropdownState.pressedKey));
    setPressedKey('');
  }

  const handleCategorySelect = useCallback(
    (option: PanelOption) => {
      const pathTemp = JSON.parse(JSON.stringify(path));
      pathTemp.push(option);

      dispatch({ type: PanelActionTypes.SET_PAGE, payload: 0 });
      dispatch({ type: PanelActionTypes.SET_FILTER_STRING, payload: '' });
      dispatch({ type: PanelActionTypes.SET_FILTER_HTML_VALUE, payload: '' });

      scrollRef.current.scrollTop = 0;
      if (onCategorySelect) {
        onCategorySelect(option);
      } else {
        dispatch({ type: PanelActionTypes.SET_PARENT_NAME, payload: option.id });
      }
      dispatch({ type: PanelActionTypes.SET_PATH, payload: pathTemp });
    },
    [path, dispatch, onCategorySelect]
  );

  const setParentName = useCallback(
    (payload: string) => dispatch({ type: PanelActionTypes.SET_PARENT_NAME, payload }),
    [dispatch]
  );

  const setPath = useCallback(
    (payload: Path[]) => dispatch({ type: PanelActionTypes.SET_PATH, payload }),
    [dispatch]
  );

  const setArrowDirection = useCallback(
    (payload: string) => dispatch({ type: PanelActionTypes.SET_ARROW_DIRECTION, payload }),
    [dispatch]
  );

  const setFilterHtmlValue = useCallback(
    (payload: string) => dispatch({ type: PanelActionTypes.SET_FILTER_HTML_VALUE, payload }),
    [dispatch]
  );

  const setFilterString = useCallback(
    (payload: string) => dispatch({ type: PanelActionTypes.SET_FILTER_STRING, payload }),
    [dispatch]
  );

  const setIsActionsFocused = useCallback(
    (payload: boolean) => dispatch({ type: PanelActionTypes.SET_IS_ACTIONS_FOCUSED, payload }),
    [dispatch]
  );

  const setPage = useCallback(
    (payload: number) => dispatch({ type: PanelActionTypes.SET_PAGE, payload }),
    [dispatch]
  );

  return useMemo(
    () => ({
      applySelection,
      arrowDirection,
      dropdownProps,
      dropdownState,
      endRef,
      filteredOptions,
      formattedOptions,
      handleCategorySelect,
      handleScroll,
      isActionsFocused,
      isFilterFocused,
      paginated,
      setParentName,
      setPath,
      setArrowDirection,
      setFilterHtmlValue,
      setFilterString,
      setIsActionsFocused,
      setPage,
      scrollRef,
      setFocusedIndex,
      setSelection,
      setIsFilterFocused,
      state,
      uniqueId,
    }),
    [
      applySelection,
      arrowDirection,
      dropdownProps,
      dropdownState,
      filteredOptions,
      formattedOptions,
      handleCategorySelect,
      handleScroll,
      isActionsFocused,
      isFilterFocused,
      paginated,
      setArrowDirection,
      setFilterHtmlValue,
      setFilterString,
      setFocusedIndex,
      setIsActionsFocused,
      setIsFilterFocused,
      setPage,
      setParentName,
      setPath,
      setSelection,
      state,
      uniqueId,
    ]
  );
};

export default usePanelState;
